/**
 * VariableChip — Custom Tiptap Node Extension
 *
 * Renders template variables (e.g. {{first_party_name}}) as inline,
 * non-editable "chip" nodes in the editor.
 *
 * Attributes: key, label, color, fontSize, bold, underline
 *
 * UX Features:
 * - Click: floating toolbar for color, font size, bold, underline, remove
 * - Right-click: quick remove
 * - Compact ✕ cross on hover (like PDF editor's FieldBox)
 */

import { Node, mergeAttributes } from '@tiptap/core';

const VariableChip = Node.create({
  name: 'variableChip',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-var-key'),
        renderHTML: (attrs) => ({ 'data-var-key': attrs.key }),
      },
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute('title') || el.textContent?.replace(/[{}]/g, ''),
        renderHTML: (attrs) => ({ title: attrs.label }),
      },
      color: {
        default: '#3730a3',
        parseHTML: (el) => el.getAttribute('data-var-color') || '#3730a3',
        renderHTML: (attrs) => ({ 'data-var-color': attrs.color }),
      },
      fontSize: {
        default: 13,
        parseHTML: (el) => parseInt(el.getAttribute('data-var-fontsize'), 10) || 13,
        renderHTML: (attrs) => ({ 'data-var-fontsize': attrs.fontSize }),
      },
      bold: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-var-bold') === 'true',
        renderHTML: (attrs) => ({ 'data-var-bold': attrs.bold ? 'true' : 'false' }),
      },
      underline: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-var-underline') === 'true',
        renderHTML: (attrs) => ({ 'data-var-underline': attrs.underline ? 'true' : 'false' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-var-key]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { color = '#3730a3', fontSize = 13, bold = false, underline: ul = false } = node.attrs;
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        contenteditable: 'false',
        'data-var-key': node.attrs.key,
        title: node.attrs.label,
        class: 'variable-chip',
        style: [
          'display:inline-block',
          'background:#e0e7ff',
          `color:${color}`,
          'padding:2px 8px',
          'border-radius:4px',
          `font-size:${fontSize}px`,
          `font-weight:${bold ? '700' : '400'}`,
          `text-decoration:${ul ? 'underline' : 'none'}`,
          'margin:0 2px',
          'cursor:default',
          'border:1px solid #a5b4fc',
          'user-select:none',
        ].join(';'),
      }),
      `{{${node.attrs.key}}}`,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      // ── Helper: update attrs on THIS specific node by position ──
      const updateNodeAttrs = (newAttrs) => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        const { tr } = editor.view.state;
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...newAttrs });
        editor.view.dispatch(tr);
      };

      // ── Chip DOM ──────────────────────────────────────────
      const dom = document.createElement('span');
      dom.className = 'variable-chip';
      dom.setAttribute('data-var-key', node.attrs.key);
      dom.contentEditable = 'false';

      const applyStyles = () => {
        const c = node.attrs.color || '#3730a3';
        const fs = node.attrs.fontSize || 13;
        const b = node.attrs.bold;
        const u = node.attrs.underline;
        Object.assign(dom.style, {
          display: 'inline-block',
          background: '#e0e7ff',
          color: c,
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: `${fs}px`,
          fontWeight: b ? '700' : '400',
          textDecoration: u ? 'underline' : 'none',
          margin: '0 2px',
          cursor: 'pointer',
          border: '1px solid #a5b4fc',
          userSelect: 'none',
          position: 'relative',
        });
      };

      applyStyles();
      dom.textContent = `{{${node.attrs.key}}}`;

      // ── Popover / Tooltip state ───────────────────────────
      let popover = null;

      const removePopover = () => {
        if (popover && popover.parentNode) popover.parentNode.removeChild(popover);
        popover = null;
      };

      // Close popover on outside click
      const outsideClickHandler = (e) => {
        if (popover && !popover.contains(e.target) && !dom.contains(e.target)) {
          removePopover();
          document.removeEventListener('mousedown', outsideClickHandler, true);
        }
      };

      // ── Helper: create icon button ────────────────────────
      const makeBtn = (innerHTML, title, active, onClick) => {
        const btn = document.createElement('button');
        btn.innerHTML = innerHTML;
        btn.title = title;
        Object.assign(btn.style, {
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: active ? '2px solid #6366f1' : '1px solid #d1d5db',
          borderRadius: '4px',
          background: active ? '#eef2ff' : '#fff',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: active ? '700' : '400',
          color: active ? '#4f46e5' : '#374151',
        });
        btn.addEventListener('mousedown', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onClick();
        });
        return btn;
      };

      // ── Click: show edit toolbar ──────────────────────────
      dom.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (popover) {
          removePopover();
          document.removeEventListener('mousedown', outsideClickHandler, true);
          return;
        }

        popover = document.createElement('div');
        Object.assign(popover.style, {
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '6px 8px',
          zIndex: '200',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
        });

        // Color picker
        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.value = node.attrs.color || '#3730a3';
        Object.assign(colorInput.style, {
          width: '28px', height: '28px',
          border: '1px solid #d1d5db', borderRadius: '4px',
          cursor: 'pointer', padding: '0',
        });
        colorInput.title = 'Text color';
        colorInput.addEventListener('input', (ev) => {
          updateNodeAttrs({ color: ev.target.value });
        });

        // Font size
        const sizeInput = document.createElement('input');
        sizeInput.type = 'number';
        sizeInput.min = '8';
        sizeInput.max = '72';
        sizeInput.value = node.attrs.fontSize || 13;
        Object.assign(sizeInput.style, {
          width: '40px', border: '1px solid #d1d5db', borderRadius: '4px',
          textAlign: 'center', fontSize: '11px', padding: '4px 2px', outline: 'none',
        });
        sizeInput.title = 'Font size (px)';
        sizeInput.addEventListener('change', (ev) => {
          updateNodeAttrs({ fontSize: parseInt(ev.target.value, 10) || 13 });
        });

        const pxLabel = document.createElement('span');
        pxLabel.textContent = 'px';
        Object.assign(pxLabel.style, { fontSize: '9px', color: '#9ca3af', marginLeft: '-2px' });

        // Separator
        const sep = () => {
          const s = document.createElement('span');
          Object.assign(s.style, { width: '1px', height: '20px', background: '#e5e7eb' });
          return s;
        };

        // Bold toggle
        const boldBtn = makeBtn('<b>B</b>', 'Bold', node.attrs.bold, () => {
          updateNodeAttrs({ bold: !node.attrs.bold });
          removePopover();
        });

        // Underline toggle
        const ulBtn = makeBtn('<u>U</u>', 'Underline', node.attrs.underline, () => {
          updateNodeAttrs({ underline: !node.attrs.underline });
          removePopover();
        });

        // Remove button
        const removeBtn = document.createElement('button');
        removeBtn.innerHTML = '✕';
        removeBtn.title = 'Remove variable';
        Object.assign(removeBtn.style, {
          width: '28px', height: '28px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#fee2e2', color: '#dc2626', border: 'none',
          borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '12px',
        });
        removeBtn.addEventListener('mousedown', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
          }
          removePopover();
        });

        popover.appendChild(colorInput);
        popover.appendChild(sizeInput);
        popover.appendChild(pxLabel);
        popover.appendChild(sep());
        popover.appendChild(boldBtn);
        popover.appendChild(ulBtn);
        popover.appendChild(sep());
        popover.appendChild(removeBtn);

        dom.appendChild(popover);

        setTimeout(() => document.addEventListener('mousedown', outsideClickHandler, true), 10);
      });

      // ── Right-click: quick remove ─────────────────────────
      dom.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        removePopover();

        const pos = getPos();
        if (typeof pos === 'number') {
          editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
        }
      });

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== 'variableChip') return false;
          // Sync DOM with updated attributes
          node = updatedNode;
          applyStyles();
          dom.childNodes.forEach((c) => {
            if (c.nodeType === Node.TEXT_NODE || (c.nodeType === 3)) {
              c.textContent = `{{${node.attrs.key}}}`;
            }
          });
          // Update the text content (first text node)
          const textNodes = Array.from(dom.childNodes).filter((c) => c.nodeType === 3);
          if (textNodes.length > 0) {
            textNodes[0].textContent = `{{${node.attrs.key}}}`;
          } else if (!popover) {
            dom.textContent = `{{${node.attrs.key}}}`;
          }
          return true;
        },
        destroy() {
          removePopover();
          document.removeEventListener('mousedown', outsideClickHandler, true);
        },
      };
    };
  },
});

export default VariableChip;
